import Linkify from "linkify-react";
import React, { type FunctionComponent, type SVGProps, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import HttpCodes from "../../../types/HttpCodes";

import { ReactComponent as FaceDisappointed } from "../../../../assets/images/face-disappointed.svg";
import { ReactComponent as HandPointRight } from "../../../../assets/images/hand-point-right.svg";
import { ReactComponent as PartyHorn } from "../../../../assets/images/party-horn.svg";
import { selectCurrentProfile } from "../../../Account/redux/profile/profileSlice";
import { Button, InvisibleButton } from "../../../Application/layout";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import styles from "./flowEnd.module.scss";
import { type FlowEndMeta } from "features/Flows/types";
import { useFeatureFlag } from "features/common/hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";

export interface Props {
  item: FlowEndMeta;
  onRestart: () => void;
}

const Icons: { [key in HttpCodes]?: FunctionComponent<SVGProps<SVGSVGElement>> } = {
  [HttpCodes.NotFound]: PartyHorn,
  [HttpCodes.Forbidden]: FaceDisappointed,
  [HttpCodes.Succeeded]: HandPointRight,
};

export const FlowEnd: React.FC<Props> = ({ item, onRestart }) => {
  const profile = useSelector(selectCurrentProfile);
  const { t } = useTranslation(["flows"]);
  const flowEndLinkEnabled = useFeatureFlag(FeatureFlags.EndOfFlowLink);
  const effectiveLink = flowEndLinkEnabled ? item.nextLinkUrl : undefined;
  const effectiveStatusCode = effectiveLink ? HttpCodes.Succeeded : item.statusCode;
  const Icon = Icons[effectiveStatusCode as HttpCodes] ?? PartyHorn;
  const baseData = useMemo(() => {
    switch (effectiveStatusCode) {
      case HttpCodes.Forbidden:
        return {
          title: t("flowEnd.forbidden.title"),
          description: t("flowEnd.forbidden.description"),
        };
      case HttpCodes.Succeeded:
        return {
          title: t("flowEnd.nextStep.title"),
          description: item.completedMessage || t("flowEnd.nextStep.description"),
        };
      case HttpCodes.NotFound:
        return {
          title: t("flowEnd.base.title"),
          description: t("flowEnd.base.description"),
        };
      default:
        return {
          title: `${t("flowEnd.base.title")}, ${profile?.firstName}!`,
          description: item.completedMessage || t("flowEnd.base.description"),
        };
    }
  }, [t, item, profile?.firstName, effectiveStatusCode]);

  const linkText = item.nextLinkLabel || item.nextLinkUrl;

  const actionElement = useMemo(() => {
    if (effectiveLink) {
      return (
        <a className={styles.nextLink} href={effectiveLink} target="_blank" rel="noopener noreferrer">
          <FontAwesomeIcon icon={faUpRightFromSquare} size="lg" />
          <div className={styles.nextLinkUrl}>{linkText}</div>
        </a>
      );
    }
    if (effectiveStatusCode === HttpCodes.Succeeded && item.nextFlowId > 0) {
      return (
        <Link tabIndex={-1} className={styles["discover-btn"]} to={`/discover/flows/${item.nextFlowId}`}>
          <Button>{item.title || t("flowEnd.nextStep.continue")}</Button>
        </Link>
      );
    }
    return (
      <Link tabIndex={-1} className={styles["discover-btn"]} to="/discover">
        <Button>{t("flowEnd.base.discoverMore")}</Button>
      </Link>
    );
  }, [effectiveLink, effectiveStatusCode, item.nextFlowId, item.title, t, linkText]);

  return (
    <div className={styles.root} data-testid="flow-end">
      <Icon />
      <h1 className={styles.title} role="alert">
        {baseData.title}
      </h1>
      <span className={styles.message}>
        <Linkify>{baseData.description}</Linkify>
      </span>

      {actionElement}

      {effectiveStatusCode === HttpCodes.NotFound && item.nextFlowId > 0 && (
        <div className={styles["restart-course"]}>
          <InvisibleButton onClick={onRestart} ariaLabel="toggle restart" data-testid="restart course button">
            {t("restartCourse.popup.content")}
          </InvisibleButton>
        </div>
      )}
    </div>
  );
};
